import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import {
  AutoFixHigh as AIIcon,
  Edit as ImproveIcon,
  Add as GenerateIcon
} from '@mui/icons-material';
import { aiEnhancementService, AIEnhancementRequest } from '../services/aiEnhancementService';

interface AIEnhancementButtonProps {
  table: string;
  recordId?: number;
  fieldName: string;
  currentValue?: string;
  formData?: Record<string, any>;
  onEnhancementResult: (enhancedContent: string) => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const AIEnhancementButton: React.FC<AIEnhancementButtonProps> = ({
  table,
  recordId,
  fieldName,
  currentValue,
  formData,
  onEnhancementResult,
  disabled = false,
  size = 'small'
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setError(null);
  };

  const handleEnhancement = async (mode: 'generate' | 'improve') => {
    try {
      setLoading(true);
      setError(null);
      handleClose();

      const request: AIEnhancementRequest = {
        table,
        recordId,
        fieldName,
        currentValue: currentValue || '',
        mode,
        context: formData
      };

      const result = await aiEnhancementService.enhanceField(request);
      onEnhancementResult(result.enhanced_content);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enhance field');
      console.error('AI Enhancement error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Tooltip title="AI is processing...">
        <span>
          <IconButton size={size} disabled>
            <CircularProgress size={size === 'small' ? 16 : 20} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title={error || "AI Enhance Field"}>
        <span>
          <IconButton
            size={size}
            onClick={handleClick}
            disabled={disabled}
            color={error ? 'error' : 'primary'}
            sx={{ ml: 0.5 }}
          >
            <AIIcon fontSize={size} />
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleEnhancement('improve')} disabled={!currentValue}>
          <ListItemIcon>
            <ImproveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Improve Content" 
            secondary="Enhance existing text"
          />
        </MenuItem>
        <MenuItem onClick={() => handleEnhancement('generate')}>
          <ListItemIcon>
            <GenerateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Generate Content" 
            secondary="Create new text"
          />
        </MenuItem>
      </Menu>
    </>
  );
}; 